import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { FormattedDate, useIntl } from "react-intl";
import { UseMutateFunction } from "react-query";
import { useUserInfo } from "@src/common/reliablePartnerApi";
import { ContactInfo, Contacts, ContactType } from "@src/details/detailApi";
import ErrorPage from "@src/ErrorPage";
import { RPFormattedMessage } from "@src/i18n";
import LazyStvUtilsFrontend from "@src/LazyStvUtilsFrontend";

// This is injected by the stv utils frontend
// and is needed for the phone number validation.
declare global {
  interface Window {
    intlTelInputUtils?: {
      isValidNumber: (phoneNumber: string, countryCode?: string) => boolean;
    };
  }
}

interface FormProps {
  contactInfo: Contacts;
  saveContactInfo: UseMutateFunction<unknown, unknown, ContactInfo>;
  submitError: boolean;
  loading: boolean;
  success: boolean;
}

interface FormValues {
  email: string;
  emailType: ContactType;
  phone: string;
  phoneType: ContactType;
}

const ContactInfoForm = ({
  contactInfo,
  saveContactInfo,
  submitError,
  loading,
  success,
}: FormProps) => {
  const getDefaultValues = (data: Contacts) => ({
    email: data.custom?.email || data.default.email || "",
    emailType: data.emailType,
    phone: data.custom?.phone || data.default.phone || "",
    phoneType: data.phoneType,
  });

  const userInfo = useUserInfo();

  const {
    formState: { isDirty, errors },
    handleSubmit,
    register,
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: getDefaultValues(contactInfo),
    resolver: values => {
      const errors: Record<string, unknown> = {};

      if (!values.phone) {
        // Admins need to be able to update info regardless of some data missing.
        if (!userInfo.data?.isAdmin) {
          errors.phone = {
            type: "required",
            message: intl.formatMessage({
              id: "contactsNoPhoneError",
              defaultMessage: "Anna puhelinnumero",
            }),
          };
        }
      } else {
        // The window.intlTelInputUtils.isValidNumber validation
        // is how it's done in the stv frontend utils
        // for the PhoneNumberInputComponent.
        // If the number does not include country code, e.g. +358,
        // we need to provide it for the validator.
        // However, we do not even know it, so we can only make a guess
        // based on the selected UI language. Although the validation
        // seemed to be identical for the supported languages in RP.
        // Bottom line: do not provide the country code if the number
        // already contains it.
        const region = values.phone.indexOf("+") === 0 ? undefined : locale();
        if (!window.intlTelInputUtils?.isValidNumber(values.phone, region)) {
          errors.phone = {
            type: "validation",
            message: intl.formatMessage({
              id: "contactsPhoneInvalidError",
              defaultMessage: "Puhelinnumero on väärässä muodossa.",
            }),
          };
        }
      }

      // eslint-disable-next-line
      const emailFormatRegex = /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/;
      if (!values.email) {
        // Admins need to be able to update info regardless of some data missing.
        if (!userInfo.data?.isAdmin) {
          errors.email = {
            type: "required",
            message: intl.formatMessage({
              id: "contactsEmailRequiredError",
              defaultMessage: "Anna sähköpostiosoite",
            }),
          };
        }
      } else if (!emailFormatRegex.test(values.email)) {
        errors.email = {
          type: "validation",
          message: intl.formatMessage({
            id: "contactsEmailInvalidError",
            defaultMessage: "Sähköpostiosoitteen tulee olla muotoa abc@def.fi",
          }),
        };
      }

      return { values, errors };
    },
  });

  const intl = useIntl();

  // needed for the phone number component, which uses country codes instead of language codes
  const locale = () => {
    switch (intl.locale) {
      case "et":
        return "ee";
      case "sv":
        return "se";
      case "en":
        return "fi"; // we don't know which country the user is from, so we default to finnish phone number
      default:
        return intl.locale;
    }
  };

  useEffect(() => reset(getDefaultValues(contactInfo)), [reset, contactInfo]);

  const onSubmit: SubmitHandler<FormValues> = ({
    email,
    emailType,
    phone,
    phoneType,
  }) =>
    saveContactInfo({
      email: emailType === ContactType.custom ? email : null,
      phone: phoneType === ContactType.custom ? phone : null,
    });

  const emailType = watch("emailType");
  const phone = watch("phone");
  const phoneType = watch("phoneType");

  const contactsUnverified = !contactInfo.updateInfo?.updatedAt;
  const defaultEmailMissing =
    emailType === ContactType.default && !contactInfo.default.email;
  const defaultPhoneMissing =
    phoneType === ContactType.default && !contactInfo.default.phone;

  return (
    <>
      <h2>
        <RPFormattedMessage id="companyTab" defaultMessage="Yhteystiedot" />
      </h2>

      {userInfo.data?.isAdmin && contactInfo.updateInfo && (
        <i>
          <RPFormattedMessage
            id="contactsUpdateInfo"
            defaultMessage="Päivitetty {updatedAt} / (<linkToUpdaterProfile>Päivittäjän tiedot</linkToUpdaterProfile>)"
            extraRTEProps={{
              linkToUpdaterProfile: {
                userId: contactInfo.updateInfo.userId,
              },
            }}
            values={{
              updatedAt: (
                <span id="contactsUpdateDate">
                  <FormattedDate
                    value={new Date(contactInfo.updateInfo.updatedAt)}
                    year="numeric"
                    month="2-digit"
                    day="2-digit"
                    hour="2-digit"
                    minute="2-digit"
                  />
                </span>
              ),
            }}
          />
        </i>
      )}

      {(contactsUnverified || defaultPhoneMissing || defaultEmailMissing) && (
        <div
          className="alert alert-info mt-5 justify-content-start d-flex"
          role="alert"
        >
          <i
            className="fa fa-exclamation-circle mr-4 align-self-center"
            aria-hidden="true"
          ></i>

          <span>
            <RPFormattedMessage
              id="contactsCheckAndConfirmAlert"
              defaultMessage={
                "Tarkista ja vahvista yhteystiedot painamalla alareunassa olevaa <b>Tallenna</b>-nappia."
              }
            />
          </span>
        </div>
      )}
      {(errors.email || errors.phone) && (
        <div
          className="alert alert-danger mt-5 justify-content-start d-flex"
          role="alert"
        >
          <i
            className="fa fa-info-circle mr-4 align-self-center"
            aria-hidden="true"
          ></i>
          <span>
            <RPFormattedMessage
              id="contactsErrorsAlert"
              defaultMessage={
                "Lomakkeella on virheitä. Tarkista vielä tiedot ja tallenna uudelleen painamalla Tallenna-nappia."
              }
            />
          </span>
        </div>
      )}
      {loading && <div className="page-loading-indicator" />}
      {success && (
        <div className="alert alert-success d-flex">
          <i className="fa fa-check fa-mr align-self-center" />
          <RPFormattedMessage
            id="contactsSaved"
            defaultMessage="Yhteystiedot tallennettu"
          />
        </div>
      )}
      {submitError && (
        <ErrorPage hideHeader={true}>
          <RPFormattedMessage
            id="contactsUpdateError"
            defaultMessage="Yhteystietojen päivitys epäonnistui. Yritä hetken päästä uudelleen"
          />
        </ErrorPage>
      )}
      <form className="my-6" onSubmit={handleSubmit(onSubmit)}>
        <h2>
          <RPFormattedMessage
            id="contactsEmailHeader"
            defaultMessage="Sähköpostiosoite"
          />
        </h2>
        <hr />
        {errors.email && (
          <div className="text-danger mt-2 font-medium" id="emailErrors">
            {errors.email.message}
          </div>
        )}
        <div className="row">
          <div className="col-12 col-md-7">
            <div className="form-group">
              <label>
                <input // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
                  className="mr-2"
                  id="emailTypeCompany"
                  type="radio"
                  value={ContactType.default}
                  onClick={() =>
                    setValue("email", contactInfo.default.email || "", {
                      shouldValidate: !!errors.email,
                    })
                  }
                  {...register("emailType")}
                />
                <RPFormattedMessage
                  id="contactsCompanyEmail"
                  defaultMessage="Käytä yleistä sähköpostiosoitetta"
                />
              </label>
              <div
                className={
                  emailType === ContactType.default ? "ml-5" : "d-none"
                }
              >
                <RPFormattedMessage
                  id="contactsChangesAtCompanyAccount"
                  defaultMessage="<b>{contactInfo}</b> (<linkToCompanyContactDetails>muutokset tähän yritystilillä</linkToCompanyContactDetails>)"
                  values={{
                    contactInfo:
                      contactInfo.default.email ||
                      intl.formatMessage({
                        id: "emailValueMissing",
                        defaultMessage: "tyhjä",
                      }),
                    b: (chunks: HTMLElement) => (
                      <b id="contactsDefaultEmail">{chunks}</b>
                    ),
                  }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <input // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
                  className="mr-2"
                  id="emailTypeCustom"
                  type="radio"
                  value={ContactType.custom}
                  {...register("emailType")}
                />
                <RPFormattedMessage
                  id="contactsCustomEmail"
                  defaultMessage="Käytä muuta sähköpostiosoitetta"
                />
              </label>
              <div
                className={emailType === ContactType.custom ? "ml-5" : "d-none"}
              >
                <input // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
                  className="form-control"
                  id="inputEmail"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: "contactsEmailPlaceholder",
                    defaultMessage: "Esim. etunimi.sukunimi@firma.com",
                  })}
                  {...register("email")}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4 font-medium">
            <i className="fa fa-info-circle mr-4 align-self-center vastuu-green" />
            <RPFormattedMessage
              id="contactsEmailInfo"
              defaultMessage={
                "Lähetämme tähän osoitteeseen kaikki raporttianne koskevat tiedotteet."
              }
            />
          </div>
        </div>

        <h2 className="pt-5">
          <RPFormattedMessage
            id="contactsPhoneHeader"
            defaultMessage="Puhelinnumero"
          />
        </h2>
        <hr />
        {errors.phone && (
          <div className="text-danger mt-2 font-medium" id="phoneErrors">
            {errors.phone.message}
          </div>
        )}
        <div className="row">
          <div className="col-12 col-md-7">
            <div className="form-group">
              <label>
                <input // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
                  className="mr-2"
                  id="phoneTypeCompany"
                  type="radio"
                  value={ContactType.default}
                  onClick={() =>
                    setValue("phone", contactInfo.default.phone || "", {
                      shouldValidate: !!errors.phone,
                    })
                  }
                  {...register("phoneType")}
                />

                <RPFormattedMessage
                  id="contactsCompanyPhone"
                  defaultMessage={"Käytä yleistä puhelinnumeroa"}
                />
              </label>
              <div
                className={
                  phoneType === ContactType.default ? "ml-5" : "d-none"
                }
              >
                <RPFormattedMessage
                  id="contactsChangesAtCompanyAccount"
                  defaultMessage="<b>{contactInfo}</b> (<linkToCompanyContactDetails>muutokset tähän yritystilillä</linkToCompanyContactDetails>)"
                  values={{
                    contactInfo:
                      contactInfo.default.phone ||
                      intl.formatMessage({
                        id: "phoneValueMissing",
                        defaultMessage: "tyhjä",
                      }),
                    b: (chunks: HTMLElement) => (
                      <b id="contactsDefaultPhone">{chunks}</b>
                    ),
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>
                <input // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
                  className="mr-2"
                  id="phoneTypeCustom"
                  type="radio"
                  value={ContactType.custom}
                  {...register("phoneType")}
                />
                <RPFormattedMessage
                  id="contactsCustomPhone"
                  defaultMessage={"Käytä muuta puhelinnumeroa"}
                />
              </label>
              <div
                className={
                  phoneType === ContactType.custom ? "ml-5 mb-5" : "d-none"
                }
              >
                <LazyStvUtilsFrontend>
                  {({ PhoneNumberInputComponent }) => (
                    <PhoneNumberInputComponent // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
                      id="contactsPhoneInput"
                      className="form-control"
                      disabled={phoneType === ContactType.default}
                      withAddon={true}
                      initialCountry={locale()}
                      value={phone}
                      preferredCountries={["fi", "se", "ee"]}
                      {...register("phone")}
                      onChange={(
                        e: React.FormEvent<HTMLInputElement> & {
                          target: { additionalInfo: { fullNumber: string } };
                        }
                      ) => {
                        setValue("phone", e.target.additionalInfo.fullNumber, {
                          shouldValidate: !!errors.phone,
                        });
                      }}
                    />
                  )}
                </LazyStvUtilsFrontend>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-4 font-medium">
            <i className="fa fa-info-circle mr-4 align-self-center vastuu-green" />
            <RPFormattedMessage
              id="contactsPhoneInfo"
              defaultMessage={
                "Puhelinnumeroa käytämme vain poikkeustilanteissa, esimerkiksi jos asia on kiireinen tai helpompi selvittää puhelimessa."
              }
            />
            <div className="my-4" />
            <b>
              <RPFormattedMessage
                id="contactsPhoneNoMarketing"
                defaultMessage="Emme tee puhelinmarkkinointia tai jaa puhelinnumeroanne edelleen."
              />
            </b>
          </div>
        </div>

        <div className="mt-6" />

        <button className="btn btn-primary" type="submit">
          <RPFormattedMessage
            id="contactsConfirm"
            defaultMessage={"Tallenna"}
          />
        </button>
        {isDirty && (
          <button
            className="btn btn-secondary ml-2"
            type="reset"
            onClick={() => reset()}
          >
            <RPFormattedMessage
              id="contactsCancelChanges"
              defaultMessage={"Peru muutokset"}
            />
          </button>
        )}
      </form>

      <div className="alert alert-secondary">
        <RPFormattedMessage
          id="contactsManageContactsText"
          defaultMessage={
            "Hallitse <b>laskutusosoitetta</b> ja muita yrityksen perustietoja <linkToCompanyAccount>yritystilillä</linkToCompanyAccount> (pääsy myös ylävalikosta yrityksen nimen alta linkistä Yrityksen tiedot)."
          }
        />
      </div>
    </>
  );
};

export default ContactInfoForm;
